import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Flame, Gift, Sparkles, Copy, Check, ShoppingBag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Daily check-in streak widget. Shows on the home page for logged-in users.
 * Encourages daily site visits by unlocking discount codes at milestones.
 */
export default function StreakWidget() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!user) { setState(null); return; }
    setLoading(true);
    api.get("/streak")
      .then((r) => setState(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (!user || loading || !state) return null;

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await api.post("/streak/checkin");
      setState({
        current: r.data.current,
        longest: r.data.longest,
        can_checkin: false,
        last_checkin: r.data.last_checkin,
        next_milestone: r.data.next_milestone,
        unlocked_codes: r.data.unlocked_codes,
      });
      if (r.data.milestone_hit) {
        const code = r.data.code;
        toast.success(`🔥 ${r.data.current}-DAY STREAK!`, {
          description: r.data.reward,
          duration: 10000,
          action: code ? {
            label: "Use Now",
            onClick: () => nav(`/shop?code=${encodeURIComponent(code)}`),
          } : undefined,
        });
      } else {
        toast.success(`Day ${r.data.current} locked in — yeehaw!`, { duration: 4000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't check in");
    } finally {
      setClaiming(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    toast.success(`Code ${code} copied`);
    setTimeout(() => setCopied(""), 2000);
  };

  const progress = state.next_milestone
    ? Math.min(100, (state.current / state.next_milestone.day) * 100)
    : 100;

  return (
    <section id="streak" className="max-w-7xl mx-auto px-4 md:px-8 py-12 scroll-mt-24" data-testid="streak-widget">
      <div className="bg-[#151A14] border-2 border-[#DD5422] p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center">
        {/* Current Streak */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <Flame className="w-16 h-16 text-[#DD5422]" strokeWidth={1.5} fill="#DD5422" fillOpacity={0.2} />
            <div data-testid="streak-current" className="absolute inset-0 flex items-center justify-center font-anton text-2xl text-[#0A0E0B]">
              {state.current}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#DD5422]">Daily Streak</div>
            <div className="font-anton text-3xl uppercase">
              {state.current} {state.current === 1 ? "Day" : "Days"}
            </div>
            <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-1">
              Best: {state.longest} day{state.longest === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Progress to next milestone */}
        <div>
          {state.next_milestone ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-[#DD5422]" />
                <div className="text-xs uppercase tracking-widest text-[#8D9688]">
                  Next reward · day {state.next_milestone.day}
                </div>
              </div>
              <div className="font-anton text-sm md:text-base uppercase tracking-tight mb-2 leading-tight">
                {state.next_milestone.reward}
              </div>
              <div className="h-2 bg-[#0A0E0B] border-2 border-[#2C332A]">
                <div className="h-full bg-[#DD5422] transition-all"
                  style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#8D9688] mt-1">
                {state.next_milestone.day - state.current} more day{state.next_milestone.day - state.current === 1 ? "" : "s"} to go
              </div>
            </>
          ) : (
            <div className="text-center">
              <Sparkles className="w-8 h-8 text-[#DD5422] mx-auto mb-2" />
              <div className="font-anton uppercase">Legend Status</div>
              <div className="text-xs text-[#8D9688] uppercase tracking-widest">All rewards unlocked.</div>
            </div>
          )}
        </div>

        {/* Action */}
        <div className="text-center md:text-right">
          {state.can_checkin ? (
            <Button data-testid="streak-checkin-btn" disabled={claiming} onClick={claim}
              className="w-full md:w-auto bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest h-14 px-8 border-2 border-[#DD5422] text-base">
              {claiming ? "Claimin'..." : "🔥 Check In Today"}
            </Button>
          ) : (
            <div data-testid="streak-already-claimed">
              <div className="font-anton text-2xl text-[#4CAF50] uppercase">✓ Locked In</div>
              <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-1">Come back tomorrow for day {state.current + 1}</div>
            </div>
          )}
        </div>
      </div>

      {/* Unlocked codes */}
      {state.unlocked_codes && state.unlocked_codes.length > 0 && (
        <div className="mt-4 bg-[#0A0E0B] border-2 border-[#2C332A] p-4">
          <div className="text-xs uppercase tracking-[0.3em] text-[#DD5422] mb-3 flex items-center gap-2">
            <Gift className="w-4 h-4" /> Your Unlocked Codes
          </div>
          <div className="flex flex-col gap-2">
            {state.unlocked_codes.map((code) => {
              const isUsed = (state.used_codes || []).map((c) => c.toUpperCase()).includes(code.toUpperCase());
              return (
                <div key={code} className="flex items-center gap-2 flex-wrap">
                  <button data-testid={`streak-code-${code}`} onClick={() => copyCode(code)}
                    disabled={isUsed}
                    className={`px-3 py-2 border-2 font-anton uppercase tracking-widest text-sm flex items-center gap-2 transition-colors ${
                      isUsed
                        ? "border-[#2C332A] bg-transparent text-[#8D9688] line-through cursor-not-allowed"
                        : "border-[#DD5422] bg-[#DD5422]/10 text-[#DD5422] hover:bg-[#DD5422] hover:text-[#0A0E0B]"
                    }`}>
                    {code}
                    {!isUsed && (copied === code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />)}
                  </button>
                  {isUsed ? (
                    <span className="text-[10px] uppercase tracking-widest text-[#8D9688]">Redeemed</span>
                  ) : (
                    <button data-testid={`streak-use-${code}`}
                      onClick={() => nav(`/shop?code=${encodeURIComponent(code)}`)}
                      className="px-3 py-2 border-2 border-[#DD5422] bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-[#FF6B35] transition-colors">
                      <ShoppingBag className="w-3 h-3" /> Use Now
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#8D9688] mt-3">
            One-time use per code. Tap "Use Now" to head straight to checkout.
          </div>
        </div>
      )}
    </section>
  );
}
